import { ArrowUpRight } from "lucide-react";
import { GOOGLE_FORM_URL, IMAGES, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { ImpactStats } from "@/components/ImpactStats";
import { FadeUp, Overline, SectionHeading } from "@/components/Reveal";

export default function AboutPage() {
  return (
    <div className="bg-jawa-ivory" data-testid="about-page">
      <Nav />
      <Seo
        title="About JÀWÀ Africa — Our Story"
        description="JÀWÀ means 'come back' in Yoruba. Founded by Elizabeth — British, of Nigerian ancestry — after personally hosting more than 70 people home, JÀWÀ Africa helps people visit, reconnect, relocate and invest in Nigeria with confidence."
      />
      <header className="relative flex min-h-[60vh] items-end overflow-hidden">
        <img src={IMAGES.journeyReturnee} alt="About JÀWÀ AFRICA" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/90 via-jawa-nearblack/40 to-jawa-nearblack/25" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-14 pt-40 lg:px-10">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.35em] text-jawa-gold">About JÀWÀ AFRICA</p>
            <h1 className="mt-3 font-serif text-5xl font-medium tracking-tight text-jawa-ivory sm:text-6xl">
              Come back. Discover Africa differently.
            </h1>
          </FadeUp>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
        <FadeUp>
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-jawa-coral">Our founder</p>
          <h2 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-jawa-espresso sm:text-5xl">
            Your connection to Nigeria deserves more than guesswork.
          </h2>
        </FadeUp>

        <div className="mt-12 grid gap-12 lg:grid-cols-[2fr_3fr] lg:gap-16">
          <FadeUp className="lg:sticky lg:top-28 lg:self-start">
            <div className="overflow-hidden border border-jawa-stone">
              <img
                src={IMAGES.elizabeth}
                alt="Elizabeth, founder of JÀWÀ AFRICA"
                className="w-full object-cover"
                data-testid="about-founder-photo"
              />
            </div>
            <p className="mt-4 font-serif text-xl italic text-jawa-espresso/80">
              "I found my way back. Then I started helping others find theirs."
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.25em] text-jawa-espresso/50">Elizabeth — Founder, JÀWÀ AFRICA</p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="space-y-5 text-base leading-relaxed text-jawa-espresso/80" data-testid="about-founder-story">
              <p className="font-serif text-2xl text-jawa-espresso">I'm Elizabeth, founder of JÀWÀ AFRICA.</p>
              <p>
                I'm British, with Nigerian ancestry, and for a long time I carried a feeling that is difficult
                to explain unless you've experienced it yourself: the sense that, despite building a successful
                life, something was still missing. Like a piece of a jigsaw puzzle I hadn't yet found.
              </p>
              <p>
                I had built a career as a senior IT consultant, working with organisations including Bank of
                America and HMRC. I also worked as an IT trainer and built businesses in real estate and
                international retail, showcasing my retail business in America and across Europe.
              </p>
              <p>
                I was comfortable solving complex problems, navigating unfamiliar environments and building
                things from the ground up. Yet something kept drawing me towards Nigeria. I wanted to reconnect
                with my heritage and understand what that connection meant to me as an adult.
              </p>
              <p>
                I didn't have parents, siblings or immediate family in Nigeria to guide me through that journey.
                I had to find my own way, learning how to navigate the country, build trusted relationships and
                work through the practical realities of being there.
              </p>
              <p className="font-serif text-xl text-jawa-espresso">But something unexpected happened along the way.</p>
              <p>
                People began asking me the same question: how had I managed to move back to Nigeria and not run back?
              </p>
              <p>
                Friends and family I had grown up with, including cousins and relatives living in the UK, Europe,
                Australia and America, were intrigued. They knew how British I was, having grown up in the UK,
                and wondered how I had made it work. If I could do it, perhaps they could too.
              </p>
              <p>
                Before long, I was seeing some of my friends and family more often in Nigeria than I had abroad.
                My home became a haven — a place where people could come, settle in, reconnect and experience
                Nigeria with someone they trusted.
              </p>
              <p>
                They came from all walks of life. Friends, former colleagues and their siblings. Uncles who had
                lived abroad for 50 years. Mixed-race cousins who hadn't visited Nigeria in more than 30 years.
                Even Nigerian aunties living abroad who had sworn they would never return home.
              </p>
              <p>One visit led to another recommendation, and then another.</p>
              <p>
                In less than two years, I had personally hosted more than 70 people. They kept coming, and they
                kept sending others my way.
              </p>
              <p>
                I began to realise that this was bigger than family and friends visiting. People wanted to
                reconnect with Nigeria, but many didn't know where to begin. They wanted someone they could trust,
                practical guidance, the right connections and reassurance that they could enjoy the experience
                without having to figure everything out alone.
              </p>
              <p>
                What had started as my own journey of reconnection had become something I was helping other
                people experience, too.
              </p>
              <p className="font-serif text-2xl text-jawa-espresso">That was how JÀWÀ AFRICA was born.</p>
              <p>
                Drawing on my background in technology, training and business, I wanted to turn that personal
                experience into something more accessible: a platform bringing together curated experiences,
                practical guides, personalised concierge services and trusted connections for people who want to
                visit, reconnect, relocate, invest or build a different future in Nigeria.
              </p>
              <p>
                JÀWÀ means "come back" in Yoruba. To me, coming back isn't only about returning to a place you've
                lived before. Sometimes it's about finding your way towards a place you've always felt drawn to,
                discovering what it means to you and helping others find their way, too.
              </p>
              <p>
                I found my way back. Then I started helping others find theirs.
              </p>
              <p className="font-serif text-xl text-jawa-espresso">
                Your journey is your own. We're here to help you make it happen.
              </p>
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mt-20">
          <div className="space-y-5 text-lg leading-relaxed text-jawa-espresso/80">
            <p>
              JÀWÀ began with a simple belief: experiencing Africa should be exciting, meaningful and full
              of possibility — not made unnecessarily difficult by uncertainty, scattered information or
              the challenge of knowing whom to trust.
            </p>
            <p>
              For many people in the diaspora, returning to Nigeria or exploring another African destination
              is about more than a holiday. It can be about reconnecting with family and culture, giving
              children a deeper connection to their heritage, exploring a business opportunity, investing in
              property or imagining a new chapter of life.
            </p>
            <p className="font-serif text-2xl text-jawa-espresso">
              But loving a place and navigating its practical realities are two different things.
            </p>
          </div>
        </FadeUp>

        <FadeUp className="mt-20">
          <ImpactStats variant="mini" />
        </FadeUp>

        <FadeUp className="mt-20">
          <SectionHeading overline="What we do" title="One platform, every step." />
          <p className="mt-6 text-base leading-relaxed text-jawa-espresso/75">
            JÀWÀ brings together travel inspiration, practical destination guides, relocation resources,
            investment-related support, curated experiences and personalised concierge assistance. Whether
            you are visiting for the first time, returning to reconnect with home, relocating with your
            family, working in Africa or exploring an investment opportunity, our aim is to help you make
            informed choices and create a journey that fits your needs.
          </p>
        </FadeUp>

        <FadeUp className="mt-16">
          <SectionHeading overline="Who we serve" title="Four journeys, one community." />
          <p className="mt-6 text-base leading-relaxed text-jawa-espresso/75">
            Tourists, diaspora returnees and relocators, international professionals and investors. Their
            needs differ, but they share a desire for better information, meaningful experiences and the
            confidence to make decisions.
          </p>
        </FadeUp>

        <FadeUp className="mt-16">
          <SectionHeading overline="What we believe" title="Our values" />
          <ul className="mt-8 space-y-6">
            {[
              ["Africa, in full colour", "Its creativity, culture, landscapes, businesses, communities and contemporary lifestyles deserve depth and authenticity."],
              ["Good information changes decisions", "Practical, relevant guidance helps people ask better questions, plan confidently and avoid preventable mistakes."],
              ["Connection matters", "Local knowledge, community and the right introductions make a complicated journey manageable."],
              ["Every journey is personal", "A relocating family, a celebrating couple, an arriving professional and an exploring investor need different support. No one-size-fits-all journeys."],
              ["Coming back is not one experience", "It may be a visit, a return to family roots, a new home, a business venture — or seeing a familiar place with fresh eyes."],
            ].map(([t, d]) => (
              <li key={t} className="border-l-4 border-jawa-turquoise bg-white p-6" data-testid={`about-value-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="font-serif text-xl font-medium text-jawa-espresso">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-jawa-espresso/70">{d}</p>
              </li>
            ))}
          </ul>
        </FadeUp>

        <FadeUp className="mt-16 grid gap-6 md:grid-cols-2">
          <div className="bg-jawa-espresso p-8">
            <Overline>Our mission</Overline>
            <p className="mt-4 font-serif text-2xl leading-snug text-jawa-ivory">
              To make discovering, experiencing, investing in and building a life in Africa more informed,
              connected and rewarding.
            </p>
          </div>
          <div className="bg-jawa-coral p-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/70">Our vision</p>
            <p className="mt-4 font-serif text-2xl leading-snug text-white">
              An Africa that people can explore with confidence, reconnect with meaningfully and engage with
              through better information, stronger connections and experiences that reflect its richness.
            </p>
          </div>
        </FadeUp>

        <FadeUp className="mt-16 border border-jawa-stone bg-white p-8 text-center lg:p-12">
          <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">JÀWÀ is an invitation.</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-jawa-espresso/70">
            Whether you are coming for a week, considering a move, building a business or reconnecting with a
            place that matters to you — we want to help you approach the journey with clarity and confidence.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href={waLink("Hello JÀWÀ — I've just read your story. Let's talk.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-whatsapp"
              className="inline-flex items-center gap-2 bg-jawa-coral px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-espresso"
            >
              Talk to the Concierge <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-bespoke"
              className="inline-flex items-center gap-2 border border-jawa-turquoise px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:bg-jawa-turquoise/10"
            >
              Plan a Bespoke Trip
            </a>
          </div>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
